import { useEffect, useState } from "react";

import { ArpFields } from "constants/forecasts.constants";
import {
  FORECAST_KEY,
  FORECAST_TRUMPING_FOLDER_ID,
  MCDANIEL_SEARCH_FOLDER_NAME as MCDANIEL_RESEARCH_FOLDER_NAME,
  SYNC_KEY,
  isForecastTrumpingSource
} from "constants/settings.constants";
import { getCumPlusForecastTitle } from "utils";

import { IGroupByListColumn } from "models";

import { isSyncFolder } from "components/sync/util";

import { useGroupByStore } from "../GroupByStoreProvider";
import {
  selectIsFiltering,
  selectIsForecastToggleOn,
  selectSelectedForecastFolder,
  selectSelectedForecastFolderName,
  selectSelectedProduct,
  selectSelectedRescat
} from "../groupBySelectors";
import { getForecastPropertySuffix } from "../helpers/qualifyForecastProperty";
import { geoVolumeCategoryTitle, padMetricsCategoryTitle } from "./useCategories";
import useSelectedCategory from "./useSelectedCategory";

/**
 * Returns the field list with forecast labelled title and property, if:
 * - performance category is selected, and
 * - forecast toggle is on
 */
const useFieldsWithForecastLabel = (
  list: IGroupByListColumn[]
): Maybe<IGroupByListColumn[]> => {
  const isForecastToggleOn = useGroupByStore(selectIsForecastToggleOn);
  const selectedProduct = useGroupByStore(selectSelectedProduct);
  const selectedForecastFolder = useGroupByStore(selectSelectedForecastFolder);
  const selectedForecastFolderName = useGroupByStore(selectSelectedForecastFolderName);
  const selectedRescat = useGroupByStore(selectSelectedRescat);
  const { hasPerformaceFields, hasForecastFields, isPadMetricsCategory } =
    useSelectedCategory();
  const isFiltering = useGroupByStore(selectIsFiltering);

  const [value, setValue] = useState<Maybe<IGroupByListColumn[]>>(null);

  useEffect(() => {
    if (!list) {
      setValue(null);
      return;
    }

    const formatItemTitleWithSource = (item: IGroupByListColumn): string => {
      let title = item.title;
      if (item.metadata?.source && !title.includes(item.metadata.source)) {
        title = `${title} (${item.metadata.source})`;
      }
      return title;
    };

    if (!hasPerformaceFields && !hasForecastFields && !isPadMetricsCategory) {
      if (isFiltering) {
        setValue(
          list.map((item) => {
            if (
              item.group !== padMetricsCategoryTitle &&
              item.group !== geoVolumeCategoryTitle
            )
              return item;
            const title = formatItemTitleWithSource(item);
            return title !== item.title ? Object.assign({}, item, { title }) : item;
          })
        );
      } else {
        setValue(list);
      }
      return;
    }

    const isSync = isSyncFolder(selectedForecastFolder);
    if (hasPerformaceFields && isSync && !isForecastToggleOn) {
      //change properties to append sync job to property
      const labelledList = list.map((item) => {
        const newItem = Object.assign({}, item, {
          property: item.property + `.${SYNC_KEY}.${selectedForecastFolder}`,
          title: item.title
        });
        return newItem;
      });
      setValue(labelledList);
      return;
    }

    if (
      !(hasPerformaceFields && isForecastToggleOn) &&
      !hasForecastFields &&
      !isPadMetricsCategory
    ) {
      setValue(list);
      return;
    }
    // Shared with the chart-axis re-stamp, so a source change that does not go through a field
    // pick still produces the identical property. Trumping includes rescat but no source, so the
    // service can pick the source per well: Performance.Oil.EUR.fcst.P+PDP
    const forecastFolderkey = getForecastPropertySuffix(
      selectedForecastFolder,
      selectedRescat
    );

    const labelledList = list.map((item) => {
      if (
        item.title.indexOf("Cum") < 5 &&
        item.title.indexOf(ArpFields.EUR) < 0 &&
        item.title.indexOf("Remaining") < 0 &&
        !item.property.includes("Cumulative") &&
        !hasForecastFields &&
        !isPadMetricsCategory
      ) {
        if (isSync && item.group === "Performance") {
          const newItem = Object.assign({}, item, {
            property: item.property + `.${SYNC_KEY}.${selectedForecastFolder}`,
            title: item.title
          });
          return newItem;
        }
        //not cum or EUR or remaining field
        return item;
      }

      // For Pad Metrics / Sampled 3D Geo items appearing in search
      if (
        isFiltering &&
        ((item.group === padMetricsCategoryTitle && !isPadMetricsCategory) ||
          item.group === geoVolumeCategoryTitle)
      ) {
        return Object.assign({}, item, { title: formatItemTitleWithSource(item) });
      }

      const newItem = Object.assign({}, item, {
        property: item.property + `.${FORECAST_KEY}${forecastFolderkey}`,
        title: isPadMetricsCategory
          ? isFiltering
            ? formatItemTitleWithSource(item)
            : item.title
          : getCumPlusForecastTitle(item.title, "", item.subgroup)
      });
      if (
        (hasForecastFields || isPadMetricsCategory) &&
        selectedForecastFolder === MCDANIEL_RESEARCH_FOLDER_NAME
      ) {
        //don't include extra if eva research
        newItem.property = item.property;
      }
      newItem.forecastFolder = isForecastTrumpingSource(selectedForecastFolder)
        ? FORECAST_TRUMPING_FOLDER_ID
        : selectedForecastFolderName;
      newItem.isForecastToggleOn = isForecastToggleOn;
      return newItem;
    });
    setValue(labelledList);
  }, [
    hasPerformaceFields,
    hasForecastFields,
    isPadMetricsCategory,
    isForecastToggleOn,
    isFiltering,
    selectedProduct,
    selectedForecastFolder,
    selectedRescat,
    list
  ]);

  return value;
};

export default useFieldsWithForecastLabel;
